import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  Camera, Plus, Trash2, Briefcase, GraduationCap, 
  Languages, Monitor, Car, Heart, Award, Phone, Mail, MapPin,
  Calendar, Building2, CheckCircle, AlertCircle, X, Pencil
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import profileService from '../../services/profileService';
import CVSectionModal from './CVSectionModal';
import { getCVLabels } from './cvLabels';
import './CVFormPreview.scss';

interface WorkExperience {
  id: string;
  companyName: string;
  companyWebsite: string;
  companyField: string;
  workField: string;
  position: string;
  description: string;
  achievements: string;
  startDate: string;
  endDate: string;
}

interface Education {
  id: string;
  educationType: string;
  degree: string;
  specialization: string;
  institution: string;
  startDate: string;
  endDate: string;
}

interface AdditionalTraining {
  id: string;
  year: string;
  duration: string;
  provider: string;
  courseName: string;
}

interface LanguageSkill {
  id: string;
  language: string;
  isNative: boolean;
  understanding: string;
  speaking: string;
  writing: string;
}

interface ComputerSkill {
  id: string;
  name: string;
  level: string;
}

interface ComputerSkills {
  office: ComputerSkill[];
  accounting: ComputerSkill[];
  os: ComputerSkill[];
  programming: ComputerSkill[];
  database: ComputerSkill[];
}

interface DrivingSkill {
  category: string;
  selected: boolean;
  experience: string;
}

interface OrganizationMembership {
  id: string;
  name: string;
  status: string;
  startDate: string;
  endDate: string;
}

interface Recommendation {
  id: string;
  company: string;
  personName: string;
  position: string;
  phone: string;
  email: string;
}

interface RestrictedCompany {
  id: string;
  name: string;
}

interface DesiredWork {
  id: string;
  location: string[];
  workField: string[];
  workType: string[];
  workTime: string[];
  positionType: string[];
  position: string[];
  salaryMin: string;
}

interface CVFormData {
  title: string;
  cvLanguage: string;
  restrictedCompanies: RestrictedCompany[];
  description: string;
  firstName: string;
  lastName: string;
  birthDate: string;
  gender: string;
  location: string;
  phone: string;
  email: string;
  profileImage: string | null;
  desiredWorks: DesiredWork[];
  desiredLocation: string;
  desiredWorkType: string;
  desiredWorkTime: string;
  desiredWorkField: string;
  desiredPositionType: string;
  desiredPosition: string;
  salaryMin: string;
  education: Education[];
  additionalTraining: AdditionalTraining[];
  workExperience: WorkExperience[];
  recommendations: Recommendation[];
  nativeLanguage: string;
  languageSkills: LanguageSkill[];
  computerSkills: ComputerSkills;
  drivingSkills: DrivingSkill[];
  drivingExperience: string;
  hasForkliftLicense: boolean;
  hasElectricForkliftLicense: boolean;
  useOwnCar: boolean;
  personalQualities: string;
  hobbies: string;
  organizationMemberships: OrganizationMembership[];
}

const DRIVING_CATEGORIES = ['A', 'B', 'C', 'D', 'E'];
const LT_MONTHS = ['Sausis','Vasaris','Kovas','Balandis','Gegužė','Birželis','Liepa','Rugpjūtis','Rugsėjis','Spalis','Lapkritis','Gruodis'];
const DATE_YEARS = Array.from({ length: 80 }, (_, i) => new Date().getFullYear() - i);

// ─── Sub-components defined OUTSIDE the parent to prevent remount on every render ───

const MonthYear: React.FC<{ value: string; onChange: (val: string) => void; minValue?: string }> = ({ value, onChange, minValue }) => {
  const parts = (value || '').split('-');
  const y = parts[0] || '';
  const m = parts[1] ? String(parseInt(parts[1])) : '';

  const minParts = (minValue || '').split('-');
  const minY = minParts[0] ? parseInt(minParts[0]) : null;
  const minM = minParts[1] ? parseInt(minParts[1]) : null;

  const update = (month: string, year: string) => {
    if (year && month) {
      onChange(`${year}-${month.padStart(2, '0')}`);
    } else if (year) {
      onChange(year);
    } else {
      onChange('');
    }
  };

  const handleYearChange = (newYear: string) => {
    if (!newYear) { onChange(''); return; }
    const monthInvalid = minY && minM && parseInt(newYear) === minY && m && parseInt(m) < minM;
    update(monthInvalid ? '' : m, newYear);
  };

  const availableYears = minY ? DATE_YEARS.filter(yr => yr >= minY) : DATE_YEARS;
  const availableMonths = (minY && minM && y && parseInt(y) === minY)
    ? LT_MONTHS.map((name, i) => ({ name, idx: i + 1 })).filter(({ idx }) => idx >= minM)
    : LT_MONTHS.map((name, i) => ({ name, idx: i + 1 }));

  return (
    <div className="date-picker-row">
      <select className="date-select date-select-year" value={y} onChange={e => handleYearChange(e.target.value)}>
        <option value="">Metai</option>
        {availableYears.map(yr => <option key={yr} value={String(yr)}>{yr}</option>)}
      </select>
      <select className="date-select" value={m} onChange={e => update(e.target.value, y)} disabled={!y}>
        <option value="">{y ? 'Mėnuo' : '–'}</option>
        {availableMonths.map(({ name, idx }) => <option key={idx} value={String(idx)}>{name}</option>)}
      </select>
    </div>
  );
};

const YearOnly: React.FC<{ value: string; onChange: (val: string) => void; minValue?: string }> = ({ value, onChange, minValue }) => {
  const y = (value || '').split('-')[0] || '';
  const minY = minValue ? parseInt((minValue || '').split('-')[0]) : null;
  const availableYears = minY ? DATE_YEARS.filter(yr => yr >= minY) : DATE_YEARS;
  return (
    <select className="date-select date-select-year" value={y} onChange={e => onChange(e.target.value || '')} style={{ width: '100%' }}>
      <option value="">Metai</option>
      {availableYears.map(yr => <option key={yr} value={String(yr)}>{yr}</option>)}
    </select>
  );
};

const MultiSelect: React.FC<{ options: string[]; selected: string[]; onChange: (v: string[]) => void; placeholder?: string }> = ({ options, selected, onChange, placeholder }) => {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const instanceId = useRef(Math.random().toString(36).slice(2));

  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    const handleOtherOpen = (e: Event) => {
      if ((e as CustomEvent).detail !== instanceId.current) setOpen(false);
    };
    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('multiselect-open', handleOtherOpen);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('multiselect-open', handleOtherOpen);
    };
  }, []);

  const handleToggle = () => {
    const next = !open;
    if (next) {
      document.dispatchEvent(new CustomEvent('multiselect-open', { detail: instanceId.current }));
    }
    setOpen(next);
  };

  const filtered = options.filter(o => o.toLowerCase().includes(search.toLowerCase()));
  const toggle = (val: string) => {
    const next = selected.includes(val) ? selected.filter(v => v !== val) : [...selected, val];
    onChange(next);
    if (!selected.includes(val)) {
      setOpen(false);
      setSearch('');
    }
  };

  return (
    <div className="multi-select" ref={ref}>
      <div className="multi-select-control" onClick={handleToggle}>
        {selected.length === 0 && <span className="multi-select-placeholder">{placeholder || 'Pasirinkite'}</span>}
        <div className="multi-select-tags">
          {selected.map(v => (
            <span key={v} className="multi-tag">
              {v}
              <button type="button" onClick={(e) => { e.stopPropagation(); toggle(v); }}>×</button>
            </span>
          ))}
        </div>
        <span className="multi-select-arrow">▾</span>
      </div>
      {open && (
        <div className="multi-select-dropdown">
          <input
            type="text"
            className="multi-select-search"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Ieškoti..."
            autoFocus
            onClick={e => e.stopPropagation()}
          />
          <div className="multi-select-options">
            {filtered.map(opt => (
              <label key={opt} className="multi-select-option">
                <input type="checkbox" checked={selected.includes(opt)} onChange={() => toggle(opt)} />
                <span>{opt}</span>
              </label>
            ))}
            {filtered.length === 0 && <div className="multi-select-empty">Nieko nerasta</div>}
          </div>
        </div>
      )}
    </div>
  );
};

const InstitutionCombobox: React.FC<{
  value: string;
  options: string[];
  onChange: (v: string) => void;
  placeholder?: string;
  pendingCategory?: string;
  searchFn?: (q: string) => Promise<string[]>;
}> = ({ value, options, onChange, placeholder, pendingCategory = 'institution', searchFn }) => {
  const [search, setSearch] = useState(value || '');
  const [open, setOpen] = useState(false);
  const [asyncOptions, setAsyncOptions] = useState<string[]>([]);
  const [asyncLoading, setAsyncLoading] = useState(false);
  const searchTimer = useRef<NodeJS.Timeout | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setSearch(value || '');
  }, [value]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
        setSearch(value || '');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [value]);

  const handleSearchChange = (q: string) => {
    setSearch(q);
    setOpen(true);
    if (!searchFn) return;
    if (searchTimer.current) clearTimeout(searchTimer.current);
    if (q.trim().length < 2) { setAsyncOptions([]); return; }
    searchTimer.current = setTimeout(async () => {
      setAsyncLoading(true);
      try {
        const results = await searchFn(q);
        setAsyncOptions(results);
      } finally {
        setAsyncLoading(false);
      }
    }, 300);
  };

  const displayOptions = searchFn ? asyncOptions : options.filter(o => o.toLowerCase().includes(search.toLowerCase()));
  const filtered = displayOptions;
  const exactMatch = displayOptions.some(o => o.toLowerCase() === search.toLowerCase());

  const handleSelect = (opt: string) => {
    onChange(opt);
    setSearch(opt);
    setOpen(false);
  };

  const handleCustomSubmit = async (val: string) => {
    const trimmed = val.trim();
    if (!trimmed || exactMatch) return;
    onChange(trimmed);
    setSearch(trimmed);
    setOpen(false);
    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      const token = localStorage.getItem('auth_token');
      await fetch(`${API_URL}/cv-templates/pending`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ category: pendingCategory, value: trimmed })
      });
    } catch { /* silently allow usage even if submission fails */ }
  };

  return (
    <div className="institution-combobox" ref={ref}>
      <input
        type="text"
        className="institution-combobox-input"
        value={search}
        placeholder={placeholder || 'Pasirinkite arba įveskite naują'}
        onChange={e => handleSearchChange(e.target.value)}
          onFocus={() => { setOpen(true); if (searchFn && !search) handleSearchChange(''); }}
        onKeyDown={e => {
          if (e.key === 'Enter' && search.trim() && !exactMatch) {
            e.preventDefault();
            handleCustomSubmit(search);
          }
          if (e.key === 'Escape') { setOpen(false); setSearch(value || ''); }
        }}
      />
      {open && (filtered.length > 0 || asyncLoading || (search.trim().length >= (searchFn ? 2 : 1) && !exactMatch)) && (
        <div className="institution-combobox-dropdown">
          <div className="combo-options">
            {asyncLoading && <div className="combo-loading">Ieškoma...</div>}
            {!asyncLoading && filtered.map(opt => (
              <div key={opt} className={`combo-option ${opt === value ? 'selected' : ''}`} onMouseDown={e => { e.preventDefault(); handleSelect(opt); }}>{opt}</div>
            ))}
            {!asyncLoading && search.trim() && !exactMatch && (
              <div className="combo-add-new" onMouseDown={e => { e.preventDefault(); handleCustomSubmit(search); }}>
                + Pridėti „{search.trim()}" <span className="combo-add-hint">(bus patvirtinta administratoriaus)</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const BulletTextarea: React.FC<{ value: string; onChange: (v: string) => void; placeholder?: string; rows?: number }> = ({ value, onChange, placeholder, rows = 4 }) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const ta = textareaRef.current;
      if (!ta) return;
      const { selectionStart } = ta;
      const before = value.slice(0, selectionStart);
      const after = value.slice(selectionStart);
      const newValue = before + '\n• ' + after;
      onChange(newValue);
      requestAnimationFrame(() => {
        ta.selectionStart = ta.selectionEnd = selectionStart + 3;
      });
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const raw = e.target.value;
    const lines = raw.split('\n');
    const formatted = lines.map(line => {
      if (line === '') return '';
      const stripped = line.replace(/^[\s•·-]*/, '').trim();
      return stripped ? `• ${stripped}` : '';
    }).join('\n');
    onChange(formatted);
  };

  const handleFocus = () => {
    if (!value) onChange('• ');
  };

  return (
    <textarea
      ref={textareaRef}
      className="bullet-textarea"
      value={value}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      onFocus={handleFocus}
      placeholder={placeholder}
      rows={rows}
    />
  );
};

// ─────────────────────────────────────────────────────────────────────────────

const CVFormPreview: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { id: cvId } = useParams<{ id: string }>();
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [cvTemplates, setCvTemplates] = useState<{ [category: string]: any[] }>({});
  const [profileImageFile, setProfileImageFile] = useState<File | null>(null);

  // Modal editing state
  const [editingSection, setEditingSection] = useState<string | null>(null);
  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const [tempData, setTempData] = useState<any>(null);

  const [formData, setFormData] = useState<CVFormData>({
    title: 'Mano CV',
    cvLanguage: 'Lietuvių',
    restrictedCompanies: [],
    description: '',
    firstName: '',
    lastName: '',
    birthDate: '',
    gender: '',
    location: '',
    phone: '',
    email: '',
    profileImage: null,
    desiredWorks: [],
    desiredLocation: '',
    desiredWorkType: '',
    desiredWorkTime: '',
    desiredWorkField: '',
    desiredPositionType: '',
    desiredPosition: '',
    salaryMin: '',
    education: [],
    additionalTraining: [],
    workExperience: [],
    recommendations: [],
    nativeLanguage: '',
    languageSkills: [],
    computerSkills: {
      office: [],
      accounting: [],
      os: [],
      programming: [],
      database: []
    },
    drivingSkills: DRIVING_CATEGORIES.map(cat => ({ category: cat, selected: false, experience: '' })),
    drivingExperience: '',
    hasForkliftLicense: false,
    hasElectricForkliftLicense: false,
    useOwnCar: false,
    personalQualities: '',
    hobbies: '',
    organizationMemberships: []
  });

  // Birth date picker state (day/month/year)
  const [bdDay, setBdDay] = useState('');
  const [bdMonth, setBdMonth] = useState('');
  const [bdYear, setBdYear] = useState('');

  // Sync birth date dropdowns when formData.birthDate changes
  useEffect(() => {
    if (formData.birthDate) {
      const parts = formData.birthDate.split('-');
      if (parts.length === 3) {
        setBdYear(parts[0]);
        setBdMonth(String(parseInt(parts[1])));
        setBdDay(String(parseInt(parts[2])));
      }
    }
  }, [formData.birthDate]);

  const handleBdChange = (day: string, month: string, year: string) => {
    if (day && month && year) {
      const mm = month.padStart(2, '0');
      const dd = day.padStart(2, '0');
      handleFieldChange('birthDate', `${year}-${mm}-${dd}`);
    } else {
      handleFieldChange('birthDate', '');
    }
  };

  const bdDaysCount = bdMonth && bdYear ? new Date(parseInt(bdYear), parseInt(bdMonth), 0).getDate() : 31;



  // Restricted companies popup state
  const [showCompanyPopup, setShowCompanyPopup] = useState(false);
  const [companySearch, setCompanySearch] = useState('');
  const companySearchInputRef = useRef<HTMLInputElement>(null);

  const searchCompanies = useCallback(async (q: string): Promise<string[]> => {
    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      const res = await fetch(`${API_URL}/companies/approved?q=${encodeURIComponent(q)}&limit=20`);
      if (!res.ok) return [];
      const data = await res.json();
      return (data.data || []).map((c: any) => c.companyName).filter(Boolean);
    } catch { return []; }
  }, []);

  // Reactive labels based on selected CV language
  const labels = useMemo(() => getCVLabels(formData.cvLanguage), [formData.cvLanguage]);
  const fmtDate = useCallback((dateStr: string) => {
    if (!dateStr) return '';
    try {
      return new Date(dateStr).toLocaleDateString(labels.dateLocale, { year: 'numeric', month: 'short' });
    } catch { return dateStr; }
  }, [labels.dateLocale]);

  const calcDuration = useCallback((startDate: string, endDate: string): string => {
    if (!startDate) return '';
    const start = new Date(startDate);
    const end = endDate ? new Date(endDate) : new Date();
    if (isNaN(start.getTime())) return '';

    let months = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());
    if (months < 0) months = 0;
    const years = Math.floor(months / 12);
    const rem = months % 12;

    const parts: string[] = [];
    if (years > 0) parts.push(`${years} m.`);
    if (rem > 0 || years === 0) parts.push(`${rem} mėn.`);
    return parts.join(' ');
  }, []);

  // =========================================
  // DATA LOADING (unchanged)
  // =========================================

  const loadCVTemplates = useCallback(async () => {
    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      const response = await fetch(`${API_URL}/cv-templates?grouped=true`);
      if (response.ok) {
        const result = await response.json();
        setCvTemplates(result.data);
      }
    } catch (error) {
      console.error('Error loading CV templates:', error);
    }
  }, []);

  const loadExistingCV = useCallback(async () => {
    if (!user || !cvId) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      const response = await fetch(`${API_URL}/cv/${cvId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        const cv = result.data;
        
        setFormData({
          title: cv.title || 'Mano CV',
          cvLanguage: ({'English': 'Anglų', 'Russian': 'Rusų', 'German': 'Vokiečių', 'Polish': 'Lenkų'} as Record<string, string>)[cv.cv_language] || cv.cv_language || 'Lietuvių',
          restrictedCompanies: cv.restricted_companies || [],
          description: cv.description || '',
          firstName: cv.first_name || '',
          lastName: cv.last_name || '',
          birthDate: cv.birth_date || '',
          gender: cv.gender || '',
          location: cv.location || cv.address || '',
          phone: cv.phone || '',
          email: cv.email || user.email || '',
          profileImage: (() => {
            const raw = cv.profile_image_url;
            if (!raw) return null;
            if (raw.startsWith('http')) return raw;
            const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
            return `${API_URL.replace('/api', '')}${raw}`;
          })(),
          desiredWorks: (() => {
            const toArr = (v: any): string[] => Array.isArray(v) ? v : (v ? [v] : []);
            if (Array.isArray(cv.job_preferences?.items) && cv.job_preferences.items.length > 0) {
              return cv.job_preferences.items.map((item: any, idx: number) => ({
                id: item.id || String(Date.now() + idx),
                location: toArr(item.workLocation || item.location),
                workField: toArr(item.workField),
                workType: toArr(item.workType),
                workTime: toArr(item.workTime),
                positionType: toArr(item.positionType),
                position: toArr(item.position),
                salaryMin: item.salaryMin || '',
              }));
            }
            const pref = cv.job_preferences;
            if (pref?.workLocation || pref?.workField || pref?.position) {
              return [{
                id: String(Date.now()),
                location: toArr(pref.workLocation),
                workField: toArr(pref.workField),
                workType: toArr(pref.workType),
                workTime: toArr(pref.workTime),
                positionType: toArr(pref.positionType),
                position: toArr(pref.position),
                salaryMin: pref.salaryMin || '',
              }];
            }
            return [];
          })(),
          desiredLocation: cv.job_preferences?.workLocation || '',
          desiredWorkType: cv.job_preferences?.workType || '',
          desiredWorkTime: cv.job_preferences?.workTime || '',
          desiredWorkField: cv.job_preferences?.workField || '',
          desiredPositionType: cv.job_preferences?.positionType || '',
          desiredPosition: cv.job_preferences?.position || '',
          salaryMin: cv.job_preferences?.salaryMin || '',
          education: cv.education || [],
          additionalTraining: cv.additional_training || [],
          workExperience: cv.work_experience || [],
          recommendations: cv.recommendations || [],
          nativeLanguage: cv.native_language || '',
          languageSkills: cv.language_skills || [],
          computerSkills: cv.computer_skills || {
            office: [],
            accounting: [],
            os: [],
            programming: [],
            database: []
          },
          drivingSkills: DRIVING_CATEGORIES.map(cat => {
            const saved = (cv.driving_skills || []).find((s: any) => s.category === cat);
            return { category: cat, selected: !!saved, experience: saved?.experience || '' };
          }),
          drivingExperience: cv.driving_skills?.[0]?.experience || '',
          hasForkliftLicense: cv.has_forklift_license || false,
          hasElectricForkliftLicense: cv.has_electric_forklift_license || false,
          useOwnCar: cv.use_own_car || false,
          personalQualities: cv.personal_qualities || '',
          hobbies: cv.hobbies || '',
          organizationMemberships: cv.organization_memberships || []
        });

      
      }
    } catch (error) {
      console.error('Error loading CV:', error);
    } finally {
      setIsLoading(false);
    }
  }, [user, cvId]);

  useEffect(() => {
    loadCVTemplates();
    loadExistingCV();
    // Companies are loaded on-demand via searchCompanies — no preload needed
  }, [loadCVTemplates, loadExistingCV]);

  // Focus search input when popup opens
  useEffect(() => {
    if (showCompanyPopup) {
      setCompanySearch('');
      setTimeout(() => companySearchInputRef.current?.focus(), 50);
    }
  }, [showCompanyPopup]);

  // After loading, always sync personal identity fields from profile (profile is source of truth)
  useEffect(() => {
    if (!user || isLoading) return;
    const profileGender = user.profile?.gender === 'male' ? 'Vyras' : user.profile?.gender === 'female' ? 'Moteris' : '';
    const profileImageUrl = profileService.getProfileImageUrl(user.profile?.profile_image || undefined);
    setFormData(prev => ({
      ...prev,
      firstName: user.first_name || prev.firstName || '',
      lastName: user.last_name || prev.lastName || '',
      email: user.email || prev.email || '',
      phone: user.phone || prev.phone || '',
      birthDate: user.profile?.birth_date || prev.birthDate || '',
      gender: profileGender || prev.gender || '',
      location: user.profile?.location || prev.location || '',
      description: user.profile?.bio || prev.description || '',
      profileImage: prev.profileImage || profileImageUrl || null,
    }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, isLoading]);

  // =========================================
  // FIELD HANDLERS (unchanged)
  // =========================================

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setProfileImageFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setFormData(prev => ({ ...prev, profileImage: e.target?.result as string }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleFieldChange = (field: keyof CVFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const addRestrictedCompany = (name: string) => {
    if (!name.trim()) return;
    if (formData.restrictedCompanies.some(c => c.name.toLowerCase() === name.toLowerCase())) return;
    setFormData(prev => ({
      ...prev,
      restrictedCompanies: [...prev.restrictedCompanies, { id: Date.now().toString(), name }]
    }));
  };

  const removeRestrictedCompany = (id: string) => {
    setFormData(prev => ({
      ...prev,
      restrictedCompanies: prev.restrictedCompanies.filter(c => c.id !== id)
    }));
  };

  const [companySuggestions, setCompanySuggestions] = useState<string[]>([]);
  const companySearchTimer = useRef<NodeJS.Timeout | null>(null);

  const handleCompanySearchChange = useCallback((q: string) => {
    setCompanySearch(q);
    if (companySearchTimer.current) clearTimeout(companySearchTimer.current);
    if (q.trim().length < 2) { setCompanySuggestions([]); return; }
    companySearchTimer.current = setTimeout(async () => {
      const results = await searchCompanies(q);
      setCompanySuggestions(results.filter(name =>
        !formData.restrictedCompanies.some(r => r.name.toLowerCase() === name.toLowerCase())
      ));
    }, 300);
  }, [searchCompanies, formData.restrictedCompanies]);

  const filteredCompanySuggestions = companySuggestions;

  const removeEducation = (id: string) => {
    setFormData(prev => ({ ...prev, education: prev.education.filter(edu => edu.id !== id) }));
  };

  const removeTraining = (id: string) => {
    setFormData(prev => ({ ...prev, additionalTraining: prev.additionalTraining.filter(t => t.id !== id) }));
  };

  const removeWorkExperience = (id: string) => {
    setFormData(prev => ({ ...prev, workExperience: prev.workExperience.filter(exp => exp.id !== id) }));
  };

  const removeRecommendation = (id: string) => {
    setFormData(prev => ({ ...prev, recommendations: prev.recommendations.filter(rec => rec.id !== id) }));
  };

  const removeOrganization = (id: string) => {
    setFormData(prev => ({
      ...prev,
      organizationMemberships: prev.organizationMemberships.filter(org => org.id !== id)
    }));
  };

  const removeDesiredWork = (id: string) => {
    setFormData(prev => ({
      ...prev,
      desiredWorks: prev.desiredWorks.filter(dw => dw.id !== id)
    }));
  };

  // =========================================
  // MODAL HELPERS
  // =========================================

  const closeModal = () => {
    setEditingSection(null);
    setEditingItemId(null);
    setTempData(null);
  };

  const updateTempField = (field: string, value: any) => {
    setTempData((prev: any) => prev ? { ...prev, [field]: value } : prev);
  };

  const openEditItem = (section: string, item: any) => {
    setEditingSection(section);
    setEditingItemId(item.id);
    setTempData({ ...item });
  };

  const openAddItem = (section: string, blankItem: any) => {
    setEditingSection(section);
    setEditingItemId(null);
    setTempData({ ...blankItem, id: Date.now().toString() });
  };

  const saveListItem = (listField: keyof CVFormData) => {
    if (!tempData) return;
    setFormData(prev => {
      const list = prev[listField] as any[];
      if (editingItemId) {
        return { ...prev, [listField]: list.map(item => item.id === editingItemId ? tempData : item) };
      }
      return { ...prev, [listField]: [...list, tempData] };
    });
    closeModal();
  };

  const calculateAge = (birthDate: string): string => {
    if (!birthDate) return '';
    const birth = new Date(birthDate);
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) age--;
    return `(${age} metai)`;
  };


  // =========================================
  // SAVE (unchanged)
  // =========================================

  const handleSave = async () => {
    if (!user) return;
    setIsSaving(true);
    setSaveMessage(null);

    try {
      const token = localStorage.getItem('auth_token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

      const backendData = {
        title: formData.title,
        cv_language: formData.cvLanguage,
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        address: formData.location,
        location: formData.location,
        birth_date: formData.birthDate,
        gender: formData.gender,
        description: formData.description,
        work_experience: formData.workExperience,
        education: formData.education,
        additional_training: formData.additionalTraining,
        native_language: formData.nativeLanguage,
        language_skills: formData.languageSkills,
        computer_skills: formData.computerSkills,
        driving_skills: formData.drivingSkills.filter(s => s.selected).map(s => ({
          category: s.category,
          experience: s.experience
        })),
        has_forklift_license: formData.hasForkliftLicense,
        has_electric_forklift_license: formData.hasElectricForkliftLicense,
        use_own_car: formData.useOwnCar,
        personal_qualities: formData.personalQualities,
        hobbies: formData.hobbies,
        organization_memberships: formData.organizationMemberships,
        recommendations: formData.recommendations,
        job_preferences: {
          items: formData.desiredWorks.map(dw => ({
            id: dw.id,
            workLocation: dw.location,
            workField: dw.workField,
            workType: dw.workType,
            workTime: dw.workTime,
            positionType: dw.positionType,
            position: dw.position,
            salaryMin: dw.salaryMin,
          })),
          workLocation: formData.desiredWorks[0]?.location?.[0] || '',
          workType: formData.desiredWorks[0]?.workType?.[0] || '',
          workTime: formData.desiredWorks[0]?.workTime?.[0] || '',
          workField: formData.desiredWorks[0]?.workField?.[0] || '',
          positionType: formData.desiredWorks[0]?.positionType?.[0] || '',
          position: formData.desiredWorks[0]?.position?.[0] || '',
          salaryMin: formData.desiredWorks[0]?.salaryMin || ''
        },
        restricted_companies: formData.restrictedCompanies.filter(c => c && c.name && c.name.trim() !== ''),
        cv_visibility_restriction: formData.restrictedCompanies.length > 0
      };

      console.log('💾 Saving CV - restricted_companies:', JSON.stringify(backendData.restricted_companies));

      let response;
      if (profileImageFile) {
        const formDataObj = new FormData();
        formDataObj.append('profile_image', profileImageFile);
        Object.entries(backendData).forEach(([key, value]) => {
          if (typeof value === 'object') {
            formDataObj.append(key, JSON.stringify(value));
          } else if (value !== null && value !== undefined) {
            formDataObj.append(key, String(value));
          }
        });

        const endpoint = cvId ? `${API_URL}/cv/${cvId}` : `${API_URL}/cv`;
        response = await fetch(endpoint, {
          method: cvId ? 'PUT' : 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formDataObj
        });
      } else {
        const endpoint = cvId ? `${API_URL}/cv/${cvId}` : `${API_URL}/cv`;
        response = await fetch(endpoint, {
          method: cvId ? 'PUT' : 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(backendData)
        });
      }

      if (response.ok) {
        setSaveMessage({ type: 'success', text: 'CV pateiktas tvirtinimui. Jis bus matomas darbdaviams po administratoriaus patvirtinimo.' });
        setTimeout(() => navigate('/mano-cv'), 1500);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to save CV');
      }
    } catch (error) {
      console.error('Error saving CV:', error);
      setSaveMessage({ type: 'error', text: 'Klaida išsaugant CV. Bandykite dar kartą.' });
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="cv-form-preview">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Kraunami CV duomenys...</p>
        </div>
      </div>
    );
  }

  const selectedDriving = formData.drivingSkills.filter(s => s.selected);
  const totalComputerSkills = Object.values(formData.computerSkills).reduce((sum, arr) => sum + arr.length, 0);

  return (
    <div className="cv-form-preview">
      {saveMessage && (
        <div className={`toast-notification ${saveMessage.type}`}>
          {saveMessage.type === 'success' ? <CheckCircle size={20} /> : <AlertCircle size={20} />}
          {saveMessage.text}
        </div>
      )}

      <div className="cv-preview-container">

        {/* ========== HEADER CARD (design: dark card, name + contact aligned) ========== */}
        <div className="cv-header-card">
          <div className="cv-header-section">
            <div className="profile-photo-area">
              <div className="photo-wrapper" onClick={() => fileInputRef.current?.click()}>
                {formData.profileImage ? (
                  <img src={formData.profileImage} alt="Profile" className="profile-photo" />
                ) : (
                  <div className="photo-placeholder">
                    <Camera size={32} />
                    <span>Pridėti nuotrauką</span>
                  </div>
                )}
                <div className="photo-overlay"><Camera size={20} /></div>
              </div>
              <input ref={fileInputRef} type="file" accept="image/*" onChange={handleImageUpload} style={{ display: 'none' }} />
            </div>

            <div className="header-info">
              <div className="header-top-row">
                <h1 className="preview-name">
                  {formData.firstName || formData.lastName
                    ? `${formData.firstName} ${formData.lastName}`.trim()
                    : <span className="placeholder-text">Vardas Pavardė</span>}
                </h1>
                <button className="edit-btn-mini" onClick={() => setEditingSection('personal')} title="Koreguoti"><Pencil size={14} /></button>
              </div>
              <p className={`preview-description ${!formData.description ? 'placeholder-text' : ''}`}>
                {formData.description || 'Pridėkite aprašymą apie save...'}
              </p>
            </div>
          </div>

          <div className="contact-bar">
            {formData.birthDate && (
              <div className="contact-item">
                <Calendar size={16} />
                <span>{new Date(formData.birthDate).toLocaleDateString('lt-LT')} {calculateAge(formData.birthDate)}</span>
              </div>
            )}
            {formData.location && (
              <div className="contact-item"><MapPin size={16} /><span>{formData.location}</span></div>
            )}
            {formData.phone && (
              <div className="contact-item"><Phone size={16} /><span>{formData.phone}</span></div>
            )}
            {formData.email && (
              <div className="contact-item"><Mail size={16} /><span>{formData.email}</span></div>
            )}
            {!formData.birthDate && !formData.location && !formData.phone && !formData.email && (
              <span className="placeholder-text" style={{ fontSize: 13 }}>Kontaktinė informacija – paspauskite „Koreguoti"</span>
            )}
          </div>
        </div>

        {/* ========== SETTINGS BAR PREVIEW ========== */}
        <div className="cv-settings-bar">
          <div className="setting-item">
            <label>CV Pavadinimas</label>
            <input type="text" value={formData.title} onChange={(e) => handleFieldChange('title', e.target.value)} placeholder="Mano CV" />
          </div>
          <div className="setting-item">
            <label>CV Kalba</label>
            <select value={formData.cvLanguage} onChange={(e) => handleFieldChange('cvLanguage', e.target.value)}>
              <option value="Lietuvių">Lietuvių</option>
              <option value="Anglų">Anglų (English)</option>
            </select>
          </div>
          <div className="setting-item setting-item--restricted">
            <label>CV nemato</label>
            <div className="restricted-inline">
              {formData.restrictedCompanies.map(company => (
                <span key={company.id} className="restricted-tag">
                  {company.name}
                  <button onClick={() => removeRestrictedCompany(company.id)}><X size={12} /></button>
                </span>
              ))}
              <button className="add-company-btn" onClick={() => setShowCompanyPopup(true)} title="Pridėti įmonę">
                <Plus size={14} /> Pridėti įmonę
              </button>
            </div>
          </div>
        </div>

        {/* ========== MAIN CONTENT SECTIONS ========== */}
        <div className="cv-main-content">

          {/* ---- WORK EXPERIENCE ---- */}
          <div className="cv-section-row">
            <div className="section-label">
              <span className="label-text">{labels.sectionWork}</span>
              <div className="label-icon"><Briefcase size={18} /></div>
            </div>
            <div className="section-data">
              {formData.workExperience.length === 0 && (
                <>
                  <p className="empty-section-hint">{labels.emptyWork}</p>
                  <button onClick={() => openAddItem('workExperience', { companyName: '', companyWebsite: '', companyField: '', workField: '', position: '', description: '', achievements: '', startDate: '', endDate: '' })} className="create-btn">{labels.btnCreate}</button>
                </>
              )}
              {formData.workExperience.map(exp => {
                const startYear = (exp.startDate || '').split('-')[0];
                const endYear = (exp.endDate || '').split('-')[0];
                const yearRange = startYear || endYear
                  ? `${startYear || '?'} – ${endYear || labels.datePresent}`
                  : null;
                return (
                  <div key={exp.id} className="preview-entry">
                    <div className="preview-entry-main">
                      {(yearRange || exp.startDate) && (
                        <div className="preview-entry-years">
                          {yearRange}
                          {exp.startDate && <span className="preview-entry-duration"> ({calcDuration(exp.startDate, exp.endDate)})</span>}
                        </div>
                      )}
                      <div className="preview-entry-title">{exp.position || '–'}</div>
                      {exp.companyName && <div className="preview-entry-subtitle">{exp.companyName}</div>}
                    </div>
                    <div className="preview-entry-actions">
                      <button onClick={() => openEditItem('workExperience', exp)} className="action-edit"><Pencil size={13} /></button>
                      <button onClick={() => removeWorkExperience(exp.id)} className="action-delete"><Trash2 size={13} /></button>
                    </div>
                  </div>
                );
              })}
              {formData.workExperience.length > 0 && <button onClick={() => openAddItem('workExperience', { companyName: '', companyWebsite: '', companyField: '', workField: '', position: '', description: '', achievements: '', startDate: '', endDate: '' })} className="create-btn">{labels.btnCreate}</button>}
            </div>
          </div>

          {/* ---- EDUCATION ---- */}
          <div className="cv-section-row">
            <div className="section-label">
              <span className="label-text">{labels.sectionEducation}</span>
              <div className="label-icon"><GraduationCap size={18} /></div>
            </div>
            <div className="section-data">
              {formData.education.length === 0 && (
                <>
                  <p className="empty-section-hint">{labels.emptyEducation}</p>
                  <button onClick={() => openAddItem('education', { educationType: '', degree: '', specialization: '', institution: '', startDate: '', endDate: '' })} className="create-btn">{labels.btnCreate}</button>
                </>
              )}
              {formData.education.map(edu => {
                const startYear = (edu.startDate || '').split('-')[0];
                const endYear = (edu.endDate || '').split('-')[0];
                const yearRange = startYear || endYear
                  ? `${startYear || '?'} – ${endYear || labels.datePresent}`
                  : null;
                return (
                  <div key={edu.id} className="preview-entry">
                    <div className="preview-entry-main">
                      {yearRange && <div className="preview-entry-years">{yearRange}</div>}
                      <div className="preview-entry-title">{edu.institution || '–'}</div>
                      {edu.educationType && <div className="preview-entry-subtitle">{edu.educationType}</div>}
                      {(edu.specialization || edu.degree) && (
                        <div className="preview-entry-subtitle">{[edu.specialization, edu.degree].filter(Boolean).join(', ')}</div>
                      )}
                    </div>
                    <div className="preview-entry-actions">
                      <button onClick={() => openEditItem('education', edu)} className="action-edit"><Pencil size={13} /></button>
                      <button onClick={() => removeEducation(edu.id)} className="action-delete"><Trash2 size={13} /></button>
                    </div>
                  </div>
                );
              })}
              {formData.education.length > 0 && <button onClick={() => openAddItem('education', { educationType: '', degree: '', specialization: '', institution: '', startDate: '', endDate: '' })} className="create-btn">{labels.btnCreate}</button>}
            </div>
          </div>

          {/* ---- ADDITIONAL TRAINING ---- */}
          <div className="cv-section-row">
            <div className="section-label">
              <span className="label-text">{labels.sectionTraining}</span>
              <div className="label-icon"><Award size={18} /></div>
            </div>
            <div className="section-data">
              {formData.additionalTraining.length === 0 && (
                <>
                  <p className="empty-section-hint">{labels.emptyTraining}</p>
                  <button onClick={() => openAddItem('training', { courseName: '', provider: '', year: '', duration: '' })} className="create-btn">{labels.btnCreate}</button>
                </>
              )}
              {formData.additionalTraining.map(t => (
                <div key={t.id} className="preview-entry">
                  <div className="preview-entry-main">
                    {t.year && <div className="preview-entry-years">{t.year}{t.duration && <span className="preview-entry-duration"> ({t.duration})</span>}</div>}
                    <div className="preview-entry-title">{t.courseName || '–'}</div>
                    {t.provider && <div className="preview-entry-subtitle">{t.provider}</div>}
                  </div>
                  <div className="preview-entry-actions">
                    <button onClick={() => openEditItem('training', t)} className="action-edit"><Pencil size={13} /></button>
                    <button onClick={() => removeTraining(t.id)} className="action-delete"><Trash2 size={13} /></button>
                  </div>
                </div>
              ))}
              {formData.additionalTraining.length > 0 && <button onClick={() => openAddItem('training', { courseName: '', provider: '', year: '', duration: '' })} className="create-btn">{labels.btnCreate}</button>}
            </div>
          </div>

          {/* ---- LANGUAGE SKILLS ---- */}
          <div className="cv-section-row">
            <div className="section-label">
              <span className="label-text">{labels.sectionLanguages}</span>
              <div className="label-icon"><Languages size={18} /></div>
            </div>
            <div className="section-data">
              {(formData.nativeLanguage || formData.languageSkills.length > 0) && (
                <button onClick={() => { setEditingSection('languages'); setTempData({ nativeLanguage: formData.nativeLanguage, languageSkills: formData.languageSkills.map(l => ({ ...l })) }); }} className="edit-btn-mini section-edit-btn" title="Koreguoti"><Pencil size={14} /></button>
              )}
              {!formData.nativeLanguage && formData.languageSkills.length === 0 && (
                <>
                  <p className="empty-section-hint">{labels.emptyLanguages}</p>
                  <button onClick={() => { setEditingSection('languages'); setTempData({ nativeLanguage: formData.nativeLanguage, languageSkills: [] }); }} className="create-btn">{labels.btnCreate}</button>
                </>
              )}
              {formData.nativeLanguage && (
                <div className="preview-tag-row">
                  <span className="preview-tag native">{formData.nativeLanguage} ({labels.langNative})</span>
                </div>
              )}
              {formData.languageSkills.length > 0 && (
                <div className="preview-lang-table">
                  <div className="preview-lang-header">
                    <span>{labels.langColLanguage}</span><span>{labels.langColUnderstanding}</span><span>{labels.langColSpeaking}</span><span>{labels.langColWriting}</span>
                  </div>
                  {formData.languageSkills.map(lang => (
                    <div key={lang.id} className="preview-lang-row">
                      <span>{lang.language || '–'}</span>
                      <span>{lang.understanding || '–'}</span>
                      <span>{lang.speaking || '–'}</span>
                      <span>{lang.writing || '–'}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* ---- COMPUTER SKILLS ---- */}
          <div className="cv-section-row">
            <div className="section-label">
              <span className="label-text">{labels.sectionComputer}</span>
              <div className="label-icon"><Monitor size={18} /></div>
            </div>
            <div className="section-data">
              {totalComputerSkills > 0 && (
                <button onClick={() => { setEditingSection('computerSkills'); setTempData({ computerSkills: JSON.parse(JSON.stringify(formData.computerSkills)) }); }} className="edit-btn-mini section-edit-btn" title="Koreguoti"><Pencil size={14} /></button>
              )}
              {totalComputerSkills === 0 && (
                <>
                  <p className="empty-section-hint">{labels.emptyComputer}</p>
                  <button onClick={() => { setEditingSection('computerSkills'); setTempData({ computerSkills: JSON.parse(JSON.stringify(formData.computerSkills)) }); }} className="create-btn">{labels.btnCreate}</button>
                </>
              )}
              {(Object.entries(formData.computerSkills) as [string, ComputerSkill[]][]).map(([cat, skills]) => {
                if (skills.length === 0) return null;
                const catLabels: Record<string, string> = { office: labels.computerOffice, accounting: labels.computerAccounting, os: labels.computerOS, programming: labels.computerProgramming, database: labels.computerDatabase };
                return (
                  <div key={cat} className="preview-skill-group">
                    <span className="preview-skill-cat">{catLabels[cat] || cat}:</span>
                    <div className="preview-tag-row">
                      {skills.map(s => <span key={s.id} className="preview-tag">{s.name}{s.level ? ` (${s.level})` : ''}</span>)}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* ---- DRIVING SKILLS ---- */}
          <div className="cv-section-row">
            <div className="section-label">
              <span className="label-text">{labels.sectionDriving}</span>
              <div className="label-icon"><Car size={18} /></div>
            </div>
            <div className="section-data">
              {(selectedDriving.length > 0 || formData.hasForkliftLicense || formData.hasElectricForkliftLicense || formData.useOwnCar) && (
                <button onClick={() => { setEditingSection('driving'); setTempData({ drivingSkills: formData.drivingSkills.map(s => ({ ...s })), drivingExperience: formData.drivingExperience, hasForkliftLicense: formData.hasForkliftLicense, hasElectricForkliftLicense: formData.hasElectricForkliftLicense, useOwnCar: formData.useOwnCar }); }} className="edit-btn-mini section-edit-btn" title="Koreguoti"><Pencil size={14} /></button>
              )}
              {selectedDriving.length === 0 && !formData.hasForkliftLicense && !formData.hasElectricForkliftLicense && !formData.useOwnCar && (
                <>
                  <p className="empty-section-hint">{labels.emptyDriving}</p>
                  <button onClick={() => { setEditingSection('driving'); setTempData({ drivingSkills: formData.drivingSkills.map(s => ({ ...s })), drivingExperience: formData.drivingExperience, hasForkliftLicense: formData.hasForkliftLicense, hasElectricForkliftLicense: formData.hasElectricForkliftLicense, useOwnCar: formData.useOwnCar }); }} className="create-btn">{labels.btnCreate}</button>
                </>
              )}
              {selectedDriving.length > 0 && (
                <div className="preview-tag-row">
                  {selectedDriving.map(s => (
                    <span key={s.category} className="preview-tag driving">
                      {s.category}{s.experience ? ` – ${s.experience} m.` : ''}
                    </span>
                  ))}
                </div>
              )}
              {formData.hasForkliftLicense && <div className="preview-detail">✓ Automobilinių krautuvų pažymėjimas</div>}
              {formData.hasElectricForkliftLicense && <div className="preview-detail">✓ Elektrinių krautuvų ir vežimėlių pažymėjimas</div>}
              {formData.useOwnCar && <div className="preview-detail">✓ {labels.drivingOwnCar}</div>}
            </div>
          </div>

          {/* ---- PERSONAL QUALITIES ---- */}
          <div className="cv-section-row">
            <div className="section-label">
              <span className="label-text">{labels.sectionQualities}</span>
              <div className="label-icon"><Heart size={18} /></div>
            </div>
            <div className="section-data">
              {formData.personalQualities && (
                <button onClick={() => setEditingSection('personalQualities')} className="edit-btn-mini section-edit-btn" title="Koreguoti"><Pencil size={14} /></button>
              )}
              {formData.personalQualities
                ? <p className="preview-text">{formData.personalQualities}</p>
                : (
                  <>
                    <p className="empty-section-hint">{labels.emptyQualities}</p>
                    <button onClick={() => setEditingSection('personalQualities')} className="create-btn">{labels.btnCreate}</button>
                  </>
                )}
            </div>
          </div>

          {/* ---- HOBBIES ---- */}
          <div className="cv-section-row">
            <div className="section-label">
              <span className="label-text">{labels.sectionHobbies}</span>
              <div className="label-icon"><Heart size={18} /></div>
            </div>
            <div className="section-data">
              {formData.hobbies && (
                <button onClick={() => setEditingSection('hobbies')} className="edit-btn-mini section-edit-btn" title="Koreguoti"><Pencil size={14} /></button>
              )}
              {formData.hobbies
                ? <p className="preview-text">{formData.hobbies}</p>
                : (
                  <>
                    <p className="empty-section-hint">{labels.emptyHobbies}</p>
                    <button onClick={() => setEditingSection('hobbies')} className="create-btn">{labels.btnCreate}</button>
                  </>
                )}
            </div>
          </div>

          {/* ---- ORGANIZATIONS ---- */}
          <div className="cv-section-row">
            <div className="section-label">
              <span className="label-text">{labels.sectionOrganizations}</span>
              <div className="label-icon"><Building2 size={18} /></div>
            </div>
            <div className="section-data">
              {formData.organizationMemberships.length === 0 && (
                <>
                  <p className="empty-section-hint">{labels.emptyOrganizations}</p>
                  <button onClick={() => openAddItem('organizations', { name: '', status: '', startDate: '', endDate: '' })} className="create-btn">{labels.btnCreate}</button>
                </>
              )}
              {formData.organizationMemberships.map(org => (
                <div key={org.id} className="preview-entry compact">
                  <div className="preview-entry-main">
                    <div className="preview-entry-title">{org.name || '–'}</div>
                    <div className="preview-entry-subtitle">{org.status || '–'}{(org.startDate || org.endDate) ? ` · ${fmtDate(org.startDate)} – ${fmtDate(org.endDate) || labels.datePresent}` : ''}</div>
                  </div>
                  <div className="preview-entry-actions">
                    <button onClick={() => openEditItem('organizations', org)} className="action-edit"><Pencil size={13} /></button>
                    <button onClick={() => removeOrganization(org.id)} className="action-delete"><Trash2 size={13} /></button>
                  </div>
                </div>
              ))}
              {formData.organizationMemberships.length > 0 && <button onClick={() => openAddItem('organizations', { name: '', status: '', startDate: '', endDate: '' })} className="create-btn">{labels.btnCreate}</button>}
            </div>
          </div>

          {/* ---- RECOMMENDATIONS ---- */}
          <div className="cv-section-row">
            <div className="section-label">
              <span className="label-text">{labels.sectionRecommendations}</span>
              <div className="label-icon"><Award size={18} /></div>
            </div>
            <div className="section-data">
              {formData.recommendations.length === 0 && (
                <>
                  <p className="empty-section-hint">{labels.emptyRecommendations}</p>
                  <button onClick={() => openAddItem('recommendations', { company: '', personName: '', position: '', phone: '', email: '' })} className="create-btn">{labels.btnCreate}</button>
                </>
              )}
              {formData.recommendations.map(rec => (
                <div key={rec.id} className="preview-entry">
                  <div className="preview-entry-main">
                    <div className="preview-entry-title">{rec.personName || '–'}</div>
                    <div className="preview-entry-subtitle">{[rec.position, rec.company].filter(Boolean).join(' · ') || '–'}</div>
                    <div className="preview-entry-contacts">{[rec.phone, rec.email].filter(Boolean).join(' · ')}</div>
                  </div>
                  <div className="preview-entry-actions">
                    <button onClick={() => openEditItem('recommendations', rec)} className="action-edit"><Pencil size={13} /></button>
                    <button onClick={() => removeRecommendation(rec.id)} className="action-delete"><Trash2 size={13} /></button>
                  </div>
                </div>
              ))}
              {formData.recommendations.length > 0 && <button onClick={() => openAddItem('recommendations', { company: '', personName: '', position: '', phone: '', email: '' })} className="create-btn">{labels.btnCreate}</button>}
            </div>
          </div>

          {/* ---- DESIRED WORK ---- */}
          <div className="cv-section-row">
            <div className="section-label">
              <span className="label-text">{labels.sectionDesiredWork}</span>
              <div className="label-icon"><Briefcase size={18} /></div>
            </div>
            <div className="section-data">
              {formData.desiredWorks.length === 0 && (
                <>
                  <p className="empty-section-hint">Nėra pageidaujamo darbo. Prašome paspausti + ir pridėti.</p>
                  <button onClick={() => openAddItem('desiredWork', { location: [], workField: [], workType: [], workTime: [], positionType: [], position: [], salaryMin: '' })} className="create-btn">{labels.btnCreate}</button>
                </>
              )}
              {formData.desiredWorks.map(dw => (
                <div key={dw.id} className="preview-entry desired-work-entry">
                  <div className="preview-entry-main">
                    {dw.position.length > 0 && (
                      <div className="dw-row">
                        <span className="dw-label">Pareigybės:</span>
                        <span className="dw-value">{dw.position.join(', ')}</span>
                      </div>
                    )}
                    {dw.workField.length > 0 && (
                      <div className="dw-row">
                        <span className="dw-label">Sritis:</span>
                        <span className="dw-value">{dw.workField.join(', ')}</span>
                      </div>
                    )}
                    {dw.location.length > 0 && (
                      <div className="dw-row">
                        <span className="dw-label">Vieta:</span>
                        <span className="dw-value">{dw.location.join(', ')}</span>
                      </div>
                    )}
                    {(dw.workType.length > 0 || dw.workTime.length > 0) && (
                      <div className="dw-row">
                        <span className="dw-label">Tipas:</span>
                        <span className="dw-value">{[...dw.workType, ...dw.workTime].join(', ')}</span>
                      </div>
                    )}
                    {dw.salaryMin && (
                      <div className="dw-row">
                        <span className="dw-label">Atlyginimas nuo:</span>
                        <span className="dw-value dw-salary">{dw.salaryMin} EUR</span>
                      </div>
                    )}
                  </div>
                  <div className="preview-entry-actions">
                    <button onClick={() => openEditItem('desiredWork', dw)} className="action-edit"><Pencil size={13} /></button>
                    <button onClick={() => removeDesiredWork(dw.id)} className="action-delete"><Trash2 size={13} /></button>
                  </div>
                </div>
              ))}
              {formData.desiredWorks.length > 0 && <button onClick={() => openAddItem('desiredWork', { location: [], workField: [], workType: [], workTime: [], positionType: [], position: [], salaryMin: '' })} className="create-btn">{labels.btnCreate}</button>}
            </div>
          </div>

        </div>

        {/* ========== BOTTOM ACTIONS ========== */}
        <div className="cv-bottom-bar">
          <div className="visibility-note">CV matomumo ribojimas įmonėms</div>
          <div className="action-buttons">
            <button className="btn btn-primary" onClick={handleSave} disabled={isSaving}>
              {isSaving ? 'Saugoma...' : 'IŠSAUGOTI'}
            </button>
          </div>
        </div>

      </div>

      {/* ===================================================
          MODALS
          =================================================== */}

      {/* --- PERSONAL INFO --- */}
      <CVSectionModal
        isOpen={editingSection === 'personal'}
        onClose={closeModal}
        onSave={closeModal}
        title="Asmeninė informacija"
        icon={<Mail size={20} />}
      >
        <div className="modal-row">
          <div className="modal-field">
            <label>Vardas</label>
            <input type="text" value={formData.firstName} onChange={(e) => handleFieldChange('firstName', e.target.value)} placeholder="Vardas" />
          </div>
          <div className="modal-field">
            <label>Pavardė</label>
            <input type="text" value={formData.lastName} onChange={(e) => handleFieldChange('lastName', e.target.value)} placeholder="Pavardė" />
          </div>
        </div>
        <div className="modal-row">
          <div className="modal-field">
            <label>Gimimo data</label>
            <div className="date-picker-row">
              <select className="date-select" value={bdDay} onChange={e => { setBdDay(e.target.value); handleBdChange(e.target.value, bdMonth, bdYear); }}>
                <option value="">DD</option>
                {Array.from({ length: bdDaysCount }, (_, i) => i + 1).map(d => (
                  <option key={d} value={String(d)}>{String(d).padStart(2,'0')}</option>
                ))}
              </select>
              <select className="date-select" value={bdMonth} onChange={e => { setBdMonth(e.target.value); handleBdChange(bdDay, e.target.value, bdYear); }}>
                <option value="">Mėnuo</option>
                {LT_MONTHS.map((m, i) => <option key={i+1} value={String(i+1)}>{m}</option>)}
              </select>
              <select className="date-select date-select-year" value={bdYear} onChange={e => { setBdYear(e.target.value); handleBdChange(bdDay, bdMonth, e.target.value); }}>
                <option value="">Metai</option>
                {Array.from({ length: 80 }, (_, i) => new Date().getFullYear() - 16 - i).map(y => (
                  <option key={y} value={String(y)}>{y}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="modal-field">
            <label>Lytis</label>
            <select value={formData.gender} onChange={(e) => handleFieldChange('gender', e.target.value)}>
              <option value="">Pasirinkite</option>
              <option value="Vyras">Vyras</option>
              <option value="Moteris">Moteris</option>
            </select>
          </div>
        </div>
        <div className="modal-field">
          <label>Miestas</label>
          <select value={formData.location} onChange={(e) => handleFieldChange('location', e.target.value)}>
            <option value="">Pasirinkite</option>
            {cvTemplates.city?.map((c: any) => <option key={c.id} value={c.value}>{c.value}</option>)}
          </select>
        </div>
        <div className="modal-row">
          <div className="modal-field">
            <label>Telefonas</label>
            <input type="tel" value={formData.phone} onChange={(e) => handleFieldChange('phone', e.target.value)} placeholder="+370..." />
          </div>
          <div className="modal-field">
            <label>El. paštas</label>
            <input type="email" value={formData.email} onChange={(e) => handleFieldChange('email', e.target.value)} placeholder="el@pastas.lt" />
          </div>
        </div>
        <div className="modal-field">
          <label>Prisistatymas</label>
          <textarea value={formData.description} onChange={(e) => handleFieldChange('description', e.target.value)} placeholder="Trumpa informacija apie mane..." rows={4} />
        </div>
      </CVSectionModal>

      {/* --- EDUCATION --- */}
      <CVSectionModal
        isOpen={editingSection === 'education'}
        onClose={closeModal}
        onSave={() => saveListItem('education')}
        title={editingItemId ? 'Redaguoti išsilavinimą' : 'Pridėti išsilavinimą'}
        icon={<GraduationCap size={20} />}
      >
        {tempData && (
          <>
            <div className="modal-row">
              <div className="modal-field">
                <label>Pradžia</label>
                <YearOnly value={tempData.startDate} onChange={(v) => updateTempField('startDate', v)} />
              </div>
              <div className="modal-field">
                <label>Pabaiga</label>
                <YearOnly value={tempData.endDate} onChange={(v) => updateTempField('endDate', v)} minValue={tempData.startDate} />
              </div>
            </div>
            <div className="modal-field">
              <label>Mokymo įstaiga</label>
              <InstitutionCombobox
                value={tempData.institution || ''}
                options={cvTemplates.institution?.map((i: any) => i.value) || []}
                onChange={(v) => updateTempField('institution', v)}
                placeholder="Pasirinkite arba įveskite naują"
              />
            </div>
            <div className="modal-field">
              <label>Išsilavinimo tipas</label>
              <select value={tempData.educationType || ''} onChange={(e) => updateTempField('educationType', e.target.value)}>
                <option value="">Pasirinkite</option>
                <option value="Aukštasis universitetinis">Aukštasis universitetinis</option>
                <option value="Aukštasis neuniversitetinis">Aukštasis neuniversitetinis</option>
                <option value="Aukštesnysis">Aukštesnysis</option>
                <option value="Profesinis">Profesinis</option>
                <option value="Vidurinis">Vidurinis</option>
                <option value="Pagrindinis">Pagrindinis</option>
                <option value="Pradinis">Pradinis</option>
              </select>
            </div>
            <div className="modal-field">
              <label>Laipsnis</label>
              <select value={tempData.degree} onChange={(e) => updateTempField('degree', e.target.value)}>
                <option value="">Pasirinkite</option>
                {cvTemplates.degree?.map((d: any) => <option key={d.id} value={d.value}>{d.value}</option>)}
              </select>
            </div>
            <div className="modal-field">
              <label>Specialybė</label>
              <input
                type="text"
                value={tempData.specialization}
                onChange={(e) => updateTempField('specialization', e.target.value)}
                placeholder="Įveskite specialybę"
              />
            </div>
          </>
        )}
      </CVSectionModal>

      {/* --- ADDITIONAL TRAINING --- */}
      <CVSectionModal
        isOpen={editingSection === 'training'}
        onClose={closeModal}
        onSave={() => saveListItem('additionalTraining')}
        title={editingItemId ? 'Redaguoti mokymą' : 'Pridėti mokymą'}
        icon={<Award size={20} />}
      >
        {tempData && (
          <>
            <div className="modal-field">
              <label>Mokymų pavadinimas</label>
              <input
                type="text"
                value={tempData.courseName}
                onChange={(e) => updateTempField('courseName', e.target.value)}
                placeholder="Kurso / mokymų pavadinimas"
              />
            </div>
            <div className="modal-row">
              <div className="modal-field">
                <label>Mokymų organizatorius</label>
                <input
                  type="text"
                  value={tempData.provider}
                  onChange={(e) => updateTempField('provider', e.target.value)}
                  placeholder="Organizacija"
                />
              </div>
              <div className="modal-field" style={{ maxWidth: 140 }}>
                <label>Metai</label>
                <select value={tempData.year} onChange={(e) => updateTempField('year', e.target.value)}>
                  <option value="">–</option>
                  {DATE_YEARS.map(y => <option key={y} value={String(y)}>{y}</option>)}
                </select>
              </div>
            </div>
            <div className="modal-field" style={{ maxWidth: 220 }}>
              <label>Mokymų trukmė</label>
              <div className="duration-input-group">
                <input
                  type="number"
                  min="0"
                  value={tempData.duration?.replace(/\D/g, '') || ''}
                  onChange={(e) => {
                    const num = e.target.value;
                    const unit = tempData.duration?.match(/(val\.|d\.)/)?.[0] || 'val.';
                    updateTempField('duration', num ? `${num} ${unit}` : '');
                  }}
                  placeholder="0"
                />
                <select
                  value={tempData.duration?.includes('d.') ? 'd.' : 'val.'}
                  onChange={(e) => {
                    const num = tempData.duration?.replace(/\D/g, '') || '';
                    updateTempField('duration', num ? `${num} ${e.target.value}` : '');
                  }}
                >
                  <option value="val.">Valandos</option>
                  <option value="d.">Dienos</option>
                </select>
              </div>
            </div>
          </>
        )}
      </CVSectionModal>

      {/* --- WORK EXPERIENCE --- */}
      <CVSectionModal
        isOpen={editingSection === 'workExperience'}
        onClose={closeModal}
        onSave={() => saveListItem('workExperience')}
        title={editingItemId ? 'Redaguoti darbo patirtį' : 'Pridėti darbo patirtį'}
        icon={<Briefcase size={20} />}
      >
        {tempData && (
          <>
            <div className="modal-row">
              <div className="modal-field">
                <label>Pradžia</label>
                <MonthYear value={tempData.startDate} onChange={(v) => updateTempField('startDate', v)} />
              </div>
              <div className="modal-field">
                <label>Pabaiga</label>
                <MonthYear value={tempData.endDate} onChange={(v) => updateTempField('endDate', v)} minValue={tempData.startDate} />
              </div>
            </div>
            <div className="modal-row">
              <div className="modal-field">
                <label>Įmonės pavadinimas</label>
                <InstitutionCombobox
                  value={tempData.companyName}
                  options={[]}
                  onChange={(v) => updateTempField('companyName', v)}
                  placeholder="Pradėkite rašyti įmonės pavadinimą..."
                  pendingCategory="company"
                  searchFn={searchCompanies}
                />
              </div>
              <div className="modal-field">
                <label>Įmonės svetainė</label>
                <input type="url" value={tempData.companyWebsite} onChange={(e) => updateTempField('companyWebsite', e.target.value)} placeholder="https://..." />
              </div>
            </div>
            <div className="modal-row">
              <div className="modal-field">
                <label>Įmonės veiklos sritis</label>
                <select value={tempData.companyField} onChange={(e) => updateTempField('companyField', e.target.value)}>
                  <option value="">Pasirinkite</option>
                  {cvTemplates.company_activity?.map((c: any) => <option key={c.id} value={c.value}>{c.value}</option>)}
                </select>
              </div>
              <div className="modal-field">
                <label>Darbo sritis</label>
                <select value={tempData.workField} onChange={(e) => updateTempField('workField', e.target.value)}>
                  <option value="">Pasirinkite</option>
                  {cvTemplates.work_field?.map((w: any) => <option key={w.id} value={w.value}>{w.value}</option>)}
                </select>
              </div>
            </div>
            <div className="modal-field">
              <label>Pareigybės</label>
              <select value={tempData.position} onChange={(e) => updateTempField('position', e.target.value)}>
                <option value="">Pasirinkite</option>
                {cvTemplates.position?.map((p: any) => <option key={p.id} value={p.value}>{p.value}</option>)}
              </select>
            </div>
            <div className="modal-field">
              <label>Darbo aprašymas</label>
              <BulletTextarea value={tempData.description} onChange={(v) => updateTempField('description', v)} placeholder="• Aprašykite darbo funkcijas..." rows={4} />
            </div>
            <div className="modal-field">
              <label>Pasiekimai</label>
              <BulletTextarea value={tempData.achievements} onChange={(v) => updateTempField('achievements', v)} placeholder="• Aprašykite pasiekimus..." rows={3} />
            </div>
          </>
        )}
      </CVSectionModal>

      {/* --- LANGUAGES --- */}
      <CVSectionModal
        isOpen={editingSection === 'languages'}
        onClose={closeModal}
        onSave={() => {
          if (!tempData) return;
          setFormData(prev => ({ ...prev, nativeLanguage: tempData.nativeLanguage, languageSkills: tempData.languageSkills }));
          closeModal();
        }}
        title="Kalbų įgūdžiai"
        icon={<Languages size={20} />}
      >
        {tempData && tempData.languageSkills !== undefined && (
          <>
            <div className="modal-field">
              <label>Gimtoji kalba</label>
              <select value={tempData.nativeLanguage} onChange={(e) => updateTempField('nativeLanguage', e.target.value)}>
                <option value="">Pasirinkite</option>
                {cvTemplates.language?.map((l: any) => (
                  <option
                    key={l.id}
                    value={l.value}
                    disabled={tempData.languageSkills.some((s: any) => s.language === l.value)}
                  >{l.value}</option>
                ))}
              </select>
            </div>

            {tempData.languageSkills.length > 0 && (
              <div className="lang-table-header">
                <span>{labels.langColLanguage}</span><span>{labels.langColUnderstanding}</span><span>{labels.langColSpeaking}</span><span>{labels.langColWriting}</span><span></span>
              </div>
            )}
            {tempData.languageSkills.map((lang: any) => {
              const usedLanguages = new Set([
                tempData.nativeLanguage,
                ...tempData.languageSkills.filter((l: any) => l.id !== lang.id).map((l: any) => l.language)
              ].filter(Boolean));
              return (
              <div key={lang.id} className="lang-row-modal">
                <select value={lang.language} onChange={(e) => updateTempField('languageSkills', tempData.languageSkills.map((l: any) => l.id === lang.id ? { ...l, language: e.target.value } : l))}>
                  <option value="">{labels.langColLanguage}</option>
                  {cvTemplates.language?.map((l: any) => (
                    <option key={l.id} value={l.value} disabled={usedLanguages.has(l.value)}>{l.value}</option>
                  ))}
                </select>
                <select value={lang.understanding} onChange={(e) => updateTempField('languageSkills', tempData.languageSkills.map((l: any) => l.id === lang.id ? { ...l, understanding: e.target.value } : l))}>
                  <option value="">–</option>
                  {labels.proficiencyLevels.map(l => <option key={l} value={l}>{l}</option>)}
                </select>
                <select value={lang.speaking} onChange={(e) => updateTempField('languageSkills', tempData.languageSkills.map((l: any) => l.id === lang.id ? { ...l, speaking: e.target.value } : l))}>
                  <option value="">–</option>
                  {labels.proficiencyLevels.map(l => <option key={l} value={l}>{l}</option>)}
                </select>
                <select value={lang.writing} onChange={(e) => updateTempField('languageSkills', tempData.languageSkills.map((l: any) => l.id === lang.id ? { ...l, writing: e.target.value } : l))}>
                  <option value="">–</option>
                  {labels.proficiencyLevels.map(l => <option key={l} value={l}>{l}</option>)}
                </select>
                <button className="remove-btn-sm" onClick={() => updateTempField('languageSkills', tempData.languageSkills.filter((l: any) => l.id !== lang.id))}><X size={14} /></button>
              </div>
              );
            })}
            <button className="add-in-modal" onClick={() => updateTempField('languageSkills', [...tempData.languageSkills, { id: Date.now().toString(), language: '', understanding: '', speaking: '', writing: '', isNative: false }])}><Plus size={14} /> Pridėti kalbą</button>
          </>
        )}
      </CVSectionModal>

      {/* --- COMPUTER SKILLS --- */}
      <CVSectionModal
        isOpen={editingSection === 'computerSkills'}
        onClose={closeModal}
        onSave={() => {
          if (!tempData) return;
          setFormData(prev => ({ ...prev, computerSkills: tempData.computerSkills }));
          closeModal();
        }}
        title="Darbo kompiuteriu įgūdžiai"
        icon={<Monitor size={20} />}
      >
        {tempData && tempData.computerSkills && ([
          ['office', labels.computerOffice, 'computer_skill_office'] as const,
          ['accounting', labels.computerAccounting, 'computer_skill_accounting'] as const,
          ['os', labels.computerOS, 'computer_skill_os'] as const,
          ['programming', labels.computerProgramming, 'computer_skill_programming'] as const,
          ['database', labels.computerDatabase, 'computer_skill_database'] as const,
        ] as const).map(([cat, label, templateKey]) => (
          <div key={cat} className="computer-skill-section">
            <h4>{label}</h4>
            {tempData.computerSkills[cat].map((skill: any) => (
              <div key={skill.id} className="skill-row-modal">
                <div className="skill-name-combo">
                  <InstitutionCombobox
                    value={skill.name}
                    options={(cvTemplates[templateKey] || []).map((s: any) => s.value)}
                    onChange={(v) => updateTempField('computerSkills', { ...tempData.computerSkills, [cat]: tempData.computerSkills[cat].map((s: any) => s.id === skill.id ? { ...s, name: v } : s) })}
                    placeholder="Pasirinkite arba įveskite"
                    pendingCategory={templateKey}
                  />
                </div>
                <select value={skill.level} onChange={(e) => updateTempField('computerSkills', { ...tempData.computerSkills, [cat]: tempData.computerSkills[cat].map((s: any) => s.id === skill.id ? { ...s, level: e.target.value } : s) })}>
                  <option value="">Lygis</option>
                  {labels.proficiencyLevels.map(l => <option key={l} value={l}>{l}</option>)}
                </select>
                <button className="remove-btn-sm" onClick={() => updateTempField('computerSkills', { ...tempData.computerSkills, [cat]: tempData.computerSkills[cat].filter((s: any) => s.id !== skill.id) })}><X size={14} /></button>
              </div>
            ))}
            <button className="add-in-modal" onClick={() => updateTempField('computerSkills', { ...tempData.computerSkills, [cat]: [...tempData.computerSkills[cat], { id: Date.now().toString(), name: '', level: '' }] })}><Plus size={14} /> Pridėti</button>
          </div>
        ))}
      </CVSectionModal>

      {/* --- DRIVING SKILLS --- */}
      <CVSectionModal
        isOpen={editingSection === 'driving'}
        onClose={closeModal}
        onSave={() => {
          if (!tempData) return;
          setFormData(prev => ({ ...prev, drivingSkills: tempData.drivingSkills, drivingExperience: tempData.drivingExperience, hasForkliftLicense: tempData.hasForkliftLicense, hasElectricForkliftLicense: tempData.hasElectricForkliftLicense, useOwnCar: tempData.useOwnCar }));
          closeModal();
        }}
        title="Vairavimo įgūdžiai"
        icon={<Car size={20} />}
      >
        {tempData && tempData.drivingSkills && (
          <>
            <div className="modal-field">
              <label>Kategorijos</label>
              <div className="category-btn-group">
                {tempData.drivingSkills.map((skill: any) => (
                  <button
                    key={skill.category}
                    type="button"
                    className={`cat-btn ${skill.selected ? 'selected' : ''}`}
                    onClick={() => updateTempField('drivingSkills', tempData.drivingSkills.map((s: any) => s.category === skill.category ? { ...s, selected: !s.selected } : s))}
                  >
                    {skill.category}
                  </button>
                ))}
              </div>
            </div>
            {tempData.drivingSkills.filter((s: any) => s.selected).length > 0 && (
              <div className="modal-field">
                <label>Vairavimo stažas (metais)</label>
                <div className="driving-experience-list">
                  {tempData.drivingSkills.filter((s: any) => s.selected).map((skill: any) => (
                    <div key={skill.category} className="driving-exp-row">
                      <span className="driving-exp-label">{skill.category}</span>
                      <input
                        type="number"
                        min="0"
                        value={skill.experience}
                        onChange={(e) => updateTempField('drivingSkills', tempData.drivingSkills.map((s: any) => s.category === skill.category ? { ...s, experience: e.target.value } : s))}
                        placeholder="0"
                      />
                      <span className="driving-exp-suffix">m.</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="checkbox-field">
              <input type="checkbox" id="forkliftLicense" checked={tempData.hasForkliftLicense} onChange={(e) => updateTempField('hasForkliftLicense', e.target.checked)} />
              <label htmlFor="forkliftLicense">Turiu automobilinių krautuvų vairuotojo kvalifikacijos pažymėjimą</label>
            </div>
            <div className="checkbox-field">
              <input type="checkbox" id="electricForkliftLicense" checked={tempData.hasElectricForkliftLicense} onChange={(e) => updateTempField('hasElectricForkliftLicense', e.target.checked)} />
              <label htmlFor="electricForkliftLicense">Turiu elektrinių krautuvų ir vežimėlių vairuotojo kvalifikacijos pažymėjimą</label>
            </div>
            <div className="checkbox-field">
              <input type="checkbox" id="ownCar" checked={tempData.useOwnCar} onChange={(e) => updateTempField('useOwnCar', e.target.checked)} />
              <label htmlFor="ownCar">Galiu naudoti nuosavą automobilį darbui</label>
            </div>
          </>
        )}
      </CVSectionModal>

      {/* --- PERSONAL QUALITIES --- */}
      <CVSectionModal
        isOpen={editingSection === 'personalQualities'}
        onClose={closeModal}
        onSave={closeModal}
        title="Asmeninės savybės"
        icon={<Heart size={20} />}
      >
        <div className="modal-field">
          <label>Aprašykite savo asmenines savybes</label>
          <textarea
            value={formData.personalQualities}
            onChange={(e) => handleFieldChange('personalQualities', e.target.value)}
            placeholder="Atsakingas, darbštus, komunikabilus..."
            rows={5}
          />
        </div>
      </CVSectionModal>

      {/* --- HOBBIES --- */}
      <CVSectionModal
        isOpen={editingSection === 'hobbies'}
        onClose={closeModal}
        onSave={closeModal}
        title="Pomėgiai, hobiai"
        icon={<Heart size={20} />}
      >
        <div className="modal-field">
          <label>Jūsų pomėgiai ir hobiai</label>
          <textarea
            value={formData.hobbies}
            onChange={(e) => handleFieldChange('hobbies', e.target.value)}
            placeholder="Sportas, kelionės, skaitymas..."
            rows={4}
          />
        </div>
      </CVSectionModal>

      {/* --- ORGANIZATIONS --- */}
      <CVSectionModal
        isOpen={editingSection === 'organizations'}
        onClose={closeModal}
        onSave={() => saveListItem('organizationMemberships')}
        title={editingItemId ? 'Redaguoti narystę' : 'Pridėti narystę'}
        icon={<Building2 size={20} />}
      >
        {tempData && (
          <>
            <div className="modal-field">
              <label>Organizacijos pavadinimas</label>
              <input type="text" value={tempData.name} onChange={(e) => updateTempField('name', e.target.value)} placeholder="Organizacija" />
            </div>
            <div className="modal-field">
              <label>Statusas organizacijoje</label>
              <input type="text" value={tempData.status} onChange={(e) => updateTempField('status', e.target.value)} placeholder="Narys / Pirmininkas..." />
            </div>
            <div className="modal-row">
              <div className="modal-field">
                <label>Pradžia</label>
                <YearOnly value={tempData.startDate} onChange={(v) => updateTempField('startDate', v)} />
              </div>
              <div className="modal-field">
                <label>Pabaiga</label>
                <YearOnly value={tempData.endDate} onChange={(v) => updateTempField('endDate', v)} minValue={tempData.startDate} />
              </div>
            </div>
          </>
        )}
      </CVSectionModal>

      {/* --- RECOMMENDATIONS --- */}
      <CVSectionModal
        isOpen={editingSection === 'recommendations'}
        onClose={closeModal}
        onSave={() => saveListItem('recommendations')}
        title={editingItemId ? 'Redaguoti rekomendaciją' : 'Pridėti rekomendaciją'}
        icon={<Award size={20} />}
      >
        {tempData && (
          <>
            <div className="modal-row">
              <div className="modal-field">
                <label>Rekomenduojantis asmuo</label>
                <input type="text" value={tempData.personName} onChange={(e) => updateTempField('personName', e.target.value)} placeholder="Vardas Pavardė" />
              </div>
              <div className="modal-field">
                <label>Įmonė</label>
                <input type="text" value={tempData.company} onChange={(e) => updateTempField('company', e.target.value)} placeholder="Įmonė" />
              </div>
            </div>
            <div className="modal-field">
              <label>Pareigos</label>
              <input type="text" value={tempData.position} onChange={(e) => updateTempField('position', e.target.value)} placeholder="Pareigos" />
            </div>
            <div className="modal-row">
              <div className="modal-field">
                <label>Telefono nr.</label>
                <input type="tel" value={tempData.phone} onChange={(e) => updateTempField('phone', e.target.value)} placeholder="+370..." />
              </div>
              <div className="modal-field">
                <label>El. paštas</label>
                <input type="email" value={tempData.email} onChange={(e) => updateTempField('email', e.target.value)} placeholder="el@pastas.lt" />
              </div>
            </div>
          </>
        )}
      </CVSectionModal>

      {/* --- DESIRED WORK --- */}
      <CVSectionModal
        isOpen={editingSection === 'desiredWork'}
        onClose={closeModal}
        onSave={() => saveListItem('desiredWorks')}
        title={editingItemId ? 'Redaguoti pageidaujamą darbą' : 'Pridėti pageidaujamą darbą'}
        icon={<Briefcase size={20} />}
      >
        {tempData && (
          <>
            <div className="modal-field">
              <label>Pageidaujama darbo vieta</label>
              <MultiSelect
                options={cvTemplates.work_location?.map((l: any) => l.value) || []}
                selected={tempData.location || []}
                onChange={(v) => updateTempField('location', v)}
                placeholder="Pasirinkite miestus"
              />
            </div>
            <div className="modal-field">
              <label>Darbo sritis</label>
              <MultiSelect
                options={cvTemplates.work_field?.map((w: any) => w.value) || []}
                selected={tempData.workField || []}
                onChange={(v) => updateTempField('workField', v)}
                placeholder="Pasirinkite darbo sritis"
              />
            </div>
            <div className="modal-row">
              <div className="modal-field">
                <label>Pareigybių tipas</label>
                <MultiSelect
                  options={cvTemplates.position_type?.map((p: any) => p.value) || []}
                  selected={tempData.positionType || []}
                  onChange={(v) => updateTempField('positionType', v)}
                  placeholder="Pasirinkite"
                />
              </div>
              <div className="modal-field">
                <label>Pageidaujamos pareigybės</label>
                <MultiSelect
                  options={cvTemplates.position?.map((p: any) => p.value) || []}
                  selected={tempData.position || []}
                  onChange={(v) => updateTempField('position', v)}
                  placeholder="Pasirinkite pareigybes"
                />
              </div>
            </div>
            <div className="modal-row">
              <div className="modal-field">
                <label>Darbo tipas</label>
                <MultiSelect
                  options={cvTemplates.work_type?.map((t: any) => t.value) || []}
                  selected={tempData.workType || []}
                  onChange={(v) => updateTempField('workType', v)}
                  placeholder="Pasirinkite"
                />
              </div>
              <div className="modal-field">
                <label>Darbo laikas</label>
                <MultiSelect
                  options={cvTemplates.work_time?.map((t: any) => t.value) || []}
                  selected={tempData.workTime || []}
                  onChange={(v) => updateTempField('workTime', v)}
                  placeholder="Pasirinkite"
                />
              </div>
            </div>
            <div className="modal-field">
              <label>Pageidaujamas atlyginimas nuo (netto) EUR</label>
              <input type="number" min="0" value={tempData.salaryMin} onChange={(e) => updateTempField('salaryMin', e.target.value)} placeholder="Suma" />
            </div>
          </>
        )}
      </CVSectionModal>

      {/* ========== COMPANY RESTRICTION POPUP ========== */}
      {showCompanyPopup && (
        <div className="company-popup-overlay" onClick={() => setShowCompanyPopup(false)}>
          <div className="company-popup" onClick={e => e.stopPropagation()}>
            <div className="company-popup-header">
              <h3>Pasirinkite įmones (CV nematys)</h3>
              <button className="company-popup-close" onClick={() => setShowCompanyPopup(false)}>
                <X size={18} />
              </button>
            </div>
            <div className="company-popup-search">
              <input
                ref={companySearchInputRef}
                type="text"
                placeholder="Ieškoti įmonės..."
                value={companySearch}
                onChange={e => handleCompanySearchChange(e.target.value)}
              />
            </div>
            {formData.restrictedCompanies.length > 0 && (
              <div className="company-popup-selected">
                <p className="company-popup-selected-label">Pasirinktos:</p>
                <div className="company-popup-tags">
                  {formData.restrictedCompanies.map(c => (
                    <span key={c.id} className="restricted-tag">
                      {c.name}
                      <button onClick={() => removeRestrictedCompany(c.id)}><X size={12} /></button>
                    </span>
                  ))}
                </div>
              </div>
            )}
            <div className="company-popup-list">
              {filteredCompanySuggestions.length === 0 ? (
                <p className="company-popup-empty">{companySearch.trim().length < 2 ? 'Įveskite bent 2 simbolius...' : 'Įmonių nerasta'}</p>
              ) : (
                filteredCompanySuggestions.map(name => (
                  <div key={name} className="company-popup-item" onClick={() => addRestrictedCompany(name)}>
                    <Plus size={14} />
                    {name}
                  </div>
                ))
              )}
            </div>
            <div className="company-popup-footer">
              <button className="company-popup-done" onClick={() => setShowCompanyPopup(false)}>
                Uždaryti
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default CVFormPreview;
